import { useMemo, useState } from "react";
import type { DeliveryBatchPayment, DeliveryQrSource } from "@mnp/shared";
import { ApiError } from "../../lib/api.js";
import { useDeliveryBatch, useDeliveryByQr } from "../../lib/useDelivery.js";
import { ClientPanel } from "./delivery/ClientPanel.js";
import { PaymentModal } from "./delivery/PaymentModal.js";
import { RefuseModal } from "./delivery/RefuseModal.js";
import { ScannerCard, type RecentScan } from "./delivery/ScannerCard.js";
import { OrderDrawer, type OrderDrawerType } from "../orders/OrderDrawer.js";

function getErrorMessage(error: unknown) {
  if (error instanceof ApiError) return error.body?.message ?? error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

export function DeliverySubscreen() {
  // qr + how it entered the UI: scanner field (strict HMAC check server-side)
  // or explicit «ввести вручную» mode (bare chatId allowed).
  const [scan, setScan] = useState<RecentScan>({ qr: "", source: "scan" });
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [openOrder, setOpenOrder] = useState<{ id: string; type: string } | null>(null);

  const deliveryQuery = useDeliveryByQr(scan.qr, scan.source);
  const batch = useDeliveryBatch();
  const payload = deliveryQuery.data ?? null;
  const selectedOrders = useMemo(
    () => payload?.orders.filter((order) => selectedIds.includes(order.orderId)) ?? [],
    [payload?.orders, selectedIds],
  );
  const selectedTotal = selectedOrders.reduce((sum, order) => sum + order.amountGel, 0);

  function handleScan(nextQr: string, source: DeliveryQrSource) {
    const clean = nextQr.trim();
    if (!clean) return;
    setScan({ qr: clean, source });
    setSelectedIds([]);
    setMessage(null);
    setRecentScans((prev) =>
      [
        { qr: clean, source },
        ...prev.filter((item) => item.qr !== clean || item.source !== source),
      ].slice(0, 5),
    );
  }

  function toggle(orderId: string) {
    setSelectedIds((prev) =>
      prev.includes(orderId) ? prev.filter((item) => item !== orderId) : [...prev, orderId],
    );
  }

  function selectAll() {
    if (!payload) return;
    // Only actionable orders are selectable: not already done/paid, and not in a
    // terminal/unverified state (BUG-02: order.selectable === false). Selecting a
    // non-deliverable order would 409 and roll back the whole batch.
    setSelectedIds(
      payload.orders
        .filter((order) => !order.paid && order.selectable !== false)
        .map((order) => order.orderId),
    );
  }

  function submitBatch(payment?: DeliveryBatchPayment, reason?: string) {
    const action = reason ? "refuse" : "deliver";
    batch.mutate(
      {
        action,
        reason,
        payment,
        items: selectedOrders.map((order) => ({ type: order.type, id: order.orderId })),
      },
      {
        onSuccess: (result) => {
          setMessage(
            action === "deliver"
              ? `Выдано: ${result.processed.length}`
              : `Оформлен отказ: ${result.processed.length}`,
          );
          setSelectedIds([]);
          setPaymentOpen(false);
          setRefuseOpen(false);
        },
        onError: (error) => setMessage(getErrorMessage(error)),
      },
    );
  }

  return (
    <div className="grid h-full gap-4 p-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <div className="space-y-4">
        <ScannerCard onScan={handleScan} recentScans={recentScans} loading={deliveryQuery.isFetching} />
        {deliveryQuery.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {getErrorMessage(deliveryQuery.error)}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700" role="status">
            {message}
          </div>
        )}
      </div>
      <ClientPanel
        payload={payload}
        selectedIds={selectedIds}
        onToggle={toggle}
        onSelectAll={selectAll}
        onDeliver={() => (selectedTotal > 0 ? setPaymentOpen(true) : submitBatch())}
        onRefuse={() => setRefuseOpen(true)}
        onOpenOrder={(o) => setOpenOrder(o)}
        loading={batch.isPending}
      />
      <PaymentModal
        open={paymentOpen}
        amountGel={selectedTotal}
        onClose={() => setPaymentOpen(false)}
        onSubmit={(payment) => submitBatch(payment)}
        loading={batch.isPending}
      />
      <RefuseModal
        open={refuseOpen}
        onClose={() => setRefuseOpen(false)}
        onSubmit={(reason) => submitBatch(undefined, reason)}
        loading={batch.isPending}
      />
      {openOrder && (
        <OrderDrawer
          id={openOrder.id}
          type={openOrder.type as OrderDrawerType}
          onClose={() => setOpenOrder(null)}
        />
      )}
    </div>
  );
}
